import api from "./api";

/** The window the rates are measured over. */
export type Period = "month" | "quarter" | "all";

export type Gender = "male" | "female" | "other" | null;

export type ReferralInfo = {
  /** `club` = the club's own code; `coach` = brought in by a member of staff. */
  source: "club" | "coach" | "parent" | "athlete" | "necunoscut";
  by_name: string | null;
  converted: boolean;
  converted_plan: string | null;
  /** What the CLUB accrued from this referral, in bani. */
  club_earned_cents: number;
};

export type PaymentInfo = {
  billed_cents: number;
  outstanding_cents: number;
  collected_cents: number;
  status: "paid" | "pending" | "overdue" | "none";
  next_due_date: string | null;
};

export type PlanInfo = {
  plan_code: string;
  status: string;
  period_end: string | null;
  paid: boolean;
};

export type Team360Athlete = {
  athlete_id: string;
  full_name: string | null;
  email: string;
  phone: string | null;
  age: number | null;
  gender: Gender;
  status: string | null;
  email_verified: boolean | null;
  points: number;
  rank: number | null;
  attendance: { present: number; absent: number; excused: number; late: number; rate: number };
  plan: PlanInfo | null;
  referral: ReferralInfo | null;
  payment: PaymentInfo | null;
};

export type Team360 = {
  team: {
    id: string;
    name: string;
    age_group: string | null;
    age_bounds: { min: number | null; max: number | null } | null;
    sport: string | null;
    created_at: string;
    club: { id: string; name: string };
  };
  coaches: { id: string; full_name: string | null; email: string; is_head: boolean }[];
  period: { key: Period; from: string | null };
  roster: {
    athletes: number;
    gender: { male: number; female: number; other: number; unknown: number };
    average_age: number | null;
    youngest: number | null;
    oldest: number | null;
    outside_age_group: number;
    without_phone: number;
    unconfirmed: number;
  };
  plans: { paid: number; free: number; rate: number };
  affiliate: {
    referred: number;
    converted: number;
    earned_cents: number;
    earned_direct_cents: number;
    earned_via_coach_cents: number;
    by_source: { club: number; coach: number; parent: number; athlete: number };
  };
  attendance: {
    present: number;
    absent: number;
    excused: number;
    rate: number;
    sessions_in_period: number;
    unmarked_sessions: number;
  };
  sessions: {
    total: number;
    in_period: number;
    upcoming: number;
    last_at: string | null;
    last_title: string | null;
    recent: { id: string; start_at: string; title: string; type: string }[];
  };
  payments: {
    billed_cents: number;
    outstanding_cents: number;
    collected_this_month_cents: number;
    overdue_athletes: number;
    without_product: number;
  };
  leaderboard: { rank: number; athlete_id: string; full_name: string; points: number }[];
  /** Waiting to be accepted onto this team; not counted in the figures above. */
  pending: {
    membership_id: string;
    athlete_id: string;
    full_name: string | null;
    email: string;
    phone: string | null;
    age: number | null;
    gender: Gender;
    requested_at: string;
    parents: { id: string; full_name: string | null; email: string; phone: string | null }[];
    source: JoinSource;
  }[];
  athletes: Team360Athlete[];
};

/** How somebody got into the club. */
export type JoinSource = {
  kind: "invitation" | "join_link" | "added_by_club" | "unknown";
  /** Who invited them, or whose link it was. */
  by_name: string | null;
  /** The link's own label, when it had one ("grupa mică"). */
  label: string | null;
};

export type Athlete360 = {
  athlete: {
    id: string;
    full_name: string | null;
    email: string;
    username: string | null;
    phone: string | null;
    age: number | null;
    date_of_birth: string | null;
    gender: Gender;
    status: string | null;
    email_verified: boolean | null;
    joined_at: string;
    profile_url: string | null;
    city: string | null;
    county: string | null;
    vulnerable: boolean;
  };
  teams: {
    id: string;
    name: string;
    age_group: string | null;
    sport: string | null;
    is_primary: boolean;
    coach: { id: string; full_name: string | null; email: string; phone: string | null } | null;
  }[];
  period: { key: Period; from: string | null };
  attendance: {
    present: number;
    absent: number;
    excused: number;
    late: number;
    rate: number;
    sessions_in_period: number;
    recent: { id: string; start_at: string; title: string; status: string | null }[];
  };
  points: {
    total: number;
    club_rank: number;
    club_athletes: number;
    recent: { id: string; delta: number; source: string; note: string | null; created_at: string }[];
  };
  plan: PlanInfo | null;
  referral: ReferralInfo | null;
  payment: PaymentInfo | null;
  parents: { id: string; full_name: string | null; email: string; phone: string | null }[];
};

export type Coach360Team = {
  id: string;
  name: string;
  age_group: string | null;
  sport: string | null;
  /** Head coach of this team, as opposed to assisting on it. */
  is_head: boolean;
  athletes: number;
  sessions_in_period: number;
  unmarked_sessions: number;
  attendance_rate: number;
  attendance_marked: number;
  outstanding_cents: number;
  overdue_athletes: number;
};

export type Coach360 = {
  coach: {
    id: string;
    full_name: string | null;
    email: string;
    phone: string | null;
    status: string | null;
    email_verified: boolean | null;
    profile_url: string | null;
    city: string | null;
    county: string | null;
    bio: string | null;
    /** When they started at THIS club, not when the account was made. */
    joined_at: string;
    membership_role: string;
    membership_status: string | null;
    is_home_club: boolean;
    sports: { id: string; name: string }[];
  };
  club: { id: string; name: string };
  period: { key: Period; from: string | null };
  roster: {
    teams: number;
    head_of: number;
    athletes: number;
    gender: { male: number; female: number; other: number; unknown: number };
    average_age: number | null;
    unconfirmed: number;
    without_phone: number;
  };
  attendance: {
    present: number;
    absent: number;
    excused: number;
    late: number;
    rate: number;
    marked: number;
    sessions_in_period: number;
    unmarked_sessions: number;
  };
  sessions: {
    total: number;
    in_period: number;
    /** Of the sessions on their teams, the ones they ran themselves. */
    ran_by_them: number;
    upcoming: number;
    last_at: string | null;
    last_title: string | null;
    last_team: string | null;
    recent: {
      id: string;
      team_id: string;
      team_name: string | null;
      start_at: string;
      title: string;
      type: string;
      marked: boolean;
      theirs: boolean;
    }[];
  };
  money: {
    outstanding_cents: number;
    overdue_athletes: number;
    collected_this_month_cents: number;
    cash_collected_cents: number;
    cash_payments: number;
    /** Cash they hold that the club has not received yet. */
    cash_pending_handover_cents: number;
    cash_pending_payments: number;
  };
  affiliate: {
    referred: number;
    converted: number;
    /** What the CLUB accrued through them. */
    club_earned_cents: number;
    /** What they accrued personally — a different pot of money. */
    own_earned_cents: number;
  };
  teams: Coach360Team[];
};

export async function getCoach360(
  coachId: string,
  opts?: { clubId?: string | null; period?: Period }
): Promise<Coach360> {
  const { data } = await api.get<Coach360>(`/club-insights/coach/${coachId}`, {
    params: {
      ...(opts?.clubId ? { club_id: opts.clubId } : {}),
      period: opts?.period || "quarter",
    },
  });
  return data;
}

export async function getTeam360(teamId: string, period: Period = "quarter"): Promise<Team360> {
  const { data } = await api.get<Team360>(`/club-insights/team/${teamId}`, { params: { period } });
  return data;
}

export async function getAthlete360(
  athleteId: string,
  opts?: { clubId?: string | null; period?: Period }
): Promise<Athlete360> {
  const { data } = await api.get<Athlete360>(`/club-insights/athlete/${athleteId}`, {
    params: {
      ...(opts?.clubId ? { club_id: opts.clubId } : {}),
      period: opts?.period || "quarter",
    },
  });
  return data;
}

/** Bani → "250,00 RON", the way the rest of the club screens read. */
export function money(cents: number): string {
  return new Intl.NumberFormat("ro-RO", {
    style: "currency",
    currency: "RON",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format((cents || 0) / 100);
}
